import React, { forwardRef, useImperativeHandle, useState } from "react";
import { Modal, Form, Input, Button, Flex, Typography, message } from "antd";
import { EditOutlined } from "@ant-design/icons";
import http from "@/utils/http";
import api from "@/utils/api";

const { Title } = Typography;

const PasswordResetModal = forwardRef((props, ref) => {
  const [form] = Form.useForm();
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [record, setRecord] = useState(null);

  const passwordRules = [
    { required: true, message: "Please enter the password." },
    { min: 6, message: "The password must be at least 6 characters." },
    { max: 20, message: "The password has a maximum of 20 characters." },
    {
      validator: (_, value) =>
        !value || !value.includes(",")
          ? Promise.resolve()
          : Promise.reject(new Error("Password cannot contain commas.")),
    },
  ];

  const validateConfirmPassword = ({ getFieldValue }) => ({
    validator(_, value) {
      if (
        !value ||
        getFieldValue("newPassword") ||
        getFieldValue("password") === value
      ) {
        return Promise.resolve();
      }
      return Promise.reject(
        new Error("The passwords entered twice are inconsistent.")
      );
    },
  });

  const onFinish = async (values) => {
    try {
      setLoading(true);
      const { _record, ...passwords } = values;
      const queryParams = {
        userId: _record.userId,
        userType: _record.userType,
        password: passwords.newPassword,
        confirmPassword: passwords.confirmPassword,
      };
      const response = await http.post(
        `${api.getAppUserUpdatePassword}`,
        queryParams
      );
      if (response.code === "1") {
        message.success("Password modified successfully.");
        setVisible(false);
        form.resetFields();
      } else {
        message.error(response.msg);
      }
    } catch (error) {
      message.error("Password modification failed.");
    } finally {
      setLoading(false);
    }
  };

  const showModal = (record) => {
    setRecord(record);
    form.setFieldsValue({
      newPassword: "",
      confirmPassword: "",
      _record: record,
    });
    setVisible(true);
  };

  const handleCancel = () => {
    setVisible(false);
    form.resetFields();
  };

  useImperativeHandle(ref, () => ({
    showModal,
  }));

  const layout = {
    labelCol: {
      style: {
        width: "150px",
      },
    },
    wrapperCol: {
      style: {
        flex: 1,
      },
    },
  };

  return (
    <Modal
      title={
        <Flex justify="flex-start" align="flex-start" className="model_title">
          <EditOutlined className="model_title_mr10" />
          <Title level={4} className="mg">
            PASSWORD RESET
          </Title>
        </Flex>
      }
      width="50%"
      open={visible}
      onCancel={handleCancel}
      footer={
        <div style={{ display: "flex", justifyContent: "center", gap: "16px" }}>
          <Button key="back" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={() => form.submit()}
          >
            Submit
          </Button>
        </div>
      }
    >
      <Flex justify="space-between" align="flex-start">
        <div className="model_top" style={{ width: "100%" }}>
          <div className="model_content">
            <Form
              {...layout}
              name="passwordResetForm"
              onFinish={onFinish}
              form={form}
            >
              <Form.Item
                name="newPassword"
                label="New Password"
                rules={passwordRules}
              >
                <Input.Password
                  placeholder="Please enter a new password."
                  allowClear
                />
              </Form.Item>
              <Form.Item
                name="confirmPassword"
                label="confirm Password"
                dependencies={["newPassword"]}
                rules={[
                  {
                    required: true,
                    message: "Please enter the password again.",
                  },
                  validateConfirmPassword,
                ]}
              >
                <Input.Password
                  placeholder="Please enter the password again."
                  allowClear
                />
              </Form.Item>
              <Form.Item name="_record" noStyle>
                <Input type="hidden" />
              </Form.Item>
            </Form>
          </div>
        </div>
      </Flex>
    </Modal>
  );
});

export default PasswordResetModal;